import { useEffect } from 'react';

export interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  type?: 'website' | 'article';
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const ensureMeta = (attr: 'name' | 'property', key: string) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  return el;
};

const ensureLink = (rel: string) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  return el;
};

const buildUrl = (path?: string) => {
  const base = typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '/';
  const prefix = base.replace(/\/$/, '');
  const rel = path ?? (window.location.pathname.replace(prefix, '') || '/');
  const normalized = rel.startsWith('/') ? rel : `/${rel}`;
  return `${window.location.origin}${prefix}${normalized === '/' ? '' : normalized}` || window.location.origin;
};

export function useSEO({
  title,
  description,
  keywords,
  canonicalPath,
  type = 'website',
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    document.title = title;
    ensureMeta('name', 'description').setAttribute('content', description);
    if (keywords) ensureMeta('name', 'keywords').setAttribute('content', keywords);
    ensureMeta('name', 'robots').setAttribute('content', 'index,follow,max-image-preview:large');
    ensureMeta('name', 'author').setAttribute('content', '黑料首页编辑部');

    const url = buildUrl(canonicalPath);
    ensureLink('canonical').setAttribute('href', url);

    ensureMeta('property', 'og:type').setAttribute('content', type);
    ensureMeta('property', 'og:site_name').setAttribute('content', '黑料首页');
    ensureMeta('property', 'og:title').setAttribute('content', title);
    ensureMeta('property', 'og:description').setAttribute('content', description);
    ensureMeta('property', 'og:url').setAttribute('content', url);
    ensureMeta('property', 'og:locale').setAttribute('content', 'zh_CN');

    ensureMeta('name', 'twitter:card').setAttribute('content', 'summary_large_image');
    ensureMeta('name', 'twitter:title').setAttribute('content', title);
    ensureMeta('name', 'twitter:description').setAttribute('content', description);

    const scriptId = 'page-structured-data';
    const previous = document.getElementById(scriptId);
    if (previous) previous.remove();

    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonicalPath, type, jsonLd]);
}